/* oxlint-disable typescript/consistent-type-assertions */
import camelcaseKeys from 'camelcase-keys';
import type { ScaError } from './sca-errors';
import { hasProperty } from './object-helpers';

/**
 * Type guard for ResponseError-like objects
 */
interface ResponseErrorLike {
  status: number;
  data: unknown;
}

function isResponseErrorLike(error: unknown): error is ResponseErrorLike {
  return hasStatusProperty(error) && hasProperty(error, 'data');
}

function hasStatusProperty(obj: unknown): obj is { status: number } {
  return hasProperty(obj, 'status') && typeof (obj as { status: unknown }).status === 'number';
}

/**
 * Extracts SCA error data from either ResponseError and EmberData network errors.
 * Converts snake_case keys to camelCase if necessary.
 */
export function extractScaErrorData(error: unknown): {
  errorData: ScaError;
  status?: number;
} {
  if (isResponseErrorLike(error)) {
    // Handle ResponseError instances - extract data and convert to camelCase
    const camelCasedData = camelcaseKeys(error.data as Record<string, unknown>, { deep: true });
    return {
      errorData: camelCasedData as ScaError,
      status: error.status,
    };
  }

  // Handle regular errors - convert to camelCase if it's an object
  if (typeof error === 'object' && error !== null) {
    const camelCasedData = camelcaseKeys(error as Record<string, unknown>, { deep: true });
    return {
      errorData: camelCasedData as ScaError,
      status: hasStatusProperty(error) ? error.status : undefined,
    };
  }

  // Return as-is for non-object errors
  return {
    errorData: error as ScaError,
  };
}
